import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap, toArray } from 'rxjs/operators';
import { accountV2Enabled } from '../site-config';
import { LegacyPlateLists } from './legacy-plate-lists';
import { AccountV2Service, SavedPlate } from './account-v2-service';
import { SessionService } from './session-service';

/**
 * "Save": the personal shortlist. Separate from Track plate (TrackPlateService)
 * and saved searches; saving never creates alerts.
 *
 * Signed in: the account's saved plates (legacy userfavourite rows, via the
 * idempotent /account/saved endpoints, so every existing favourite shows).
 * Signed out: saved in this browser (localStorage) with no sign-in needed, and
 * merged into the account without duplicates the next time the visitor signs
 * in (POST /account/saved/merge), then cleared locally.
 *
 * One shared set of saved registrations so every heart on a page agrees.
 *
 * Release hosts (staging, www) use the legacy favourites list instead
 * (LegacyPlateLists): the production API has no /account/saved endpoints.
 */
@Injectable({ providedIn: 'root' })
export class SavedPlatesService {
  private static readonly GUEST_KEY = 'pla-guest-saved';
  private readonly saved$ = new BehaviorSubject<Set<string>>(new Set());
  private loadedSignedIn = false;
  private inFlight = new Set<string>();

  private readonly legacy: LegacyPlateLists | null;

  constructor(
    private account: AccountV2Service,
    private session: SessionService,
    http: HttpClient
  ) {
    this.legacy = accountV2Enabled ? null : new LegacyPlateLists(http);
    this.session.sessionStateChange.subscribe(() => this.refresh(true));
    this.refresh(false);
  }

  public static canonical(reg: string | null | undefined): string {
    return (reg || '').replace(/\s+/g, '').toUpperCase();
  }

  public get changes(): Observable<Set<string>> {
    return this.saved$.asObservable();
  }

  public get count(): number {
    return this.saved$.value.size;
  }

  public isSaved(reg: string | null | undefined): boolean {
    return this.saved$.value.has(SavedPlatesService.canonical(reg));
  }

  public isBusy(reg: string | null | undefined): boolean {
    return this.inFlight.has(SavedPlatesService.canonical(reg));
  }

  /** Reloads the saved set; on sign-in first merges any guest saves. */
  public refresh(force: boolean): void {
    if (!this.session.isLoggedIn()) {
      this.loadedSignedIn = false;
      this.saved$.next(new Set(this.readGuest()));
      return;
    }
    if (!force && this.loadedSignedIn) return;
    this.loadedSignedIn = true;
    const guest = this.readGuest();
    if (this.legacy) {
      this.refreshLegacy(this.legacy, guest);
      return;
    }
    const load$: Observable<SavedPlate[]> = guest.length
      ? this.account.mergeSaved(guest).pipe(
          tap(() => this.writeGuest([])),
          map((r) => r.saved)
        )
      : this.account.getSaved();
    load$
      .pipe(catchError(() => of([] as SavedPlate[])))
      .subscribe((rows) =>
        this.saved$.next(new Set(rows.map((r) => SavedPlatesService.canonical(r.registration))))
      );
  }

  /** Legacy favourites, then any guest saves not already in them (one toggle each). */
  private refreshLegacy(legacy: LegacyPlateLists, guest: string[]): void {
    legacy
      .favourites()
      .pipe(
        concatMap((have) => {
          const add = guest.filter((g) => !have.includes(g));
          return from(add).pipe(
            concatMap((r) => legacy.toggleFavourite(r).pipe(map((ok) => (ok ? r : '')))),
            toArray(),
            map((added) => [...have, ...added.filter(Boolean)])
          );
        }),
        tap(() => this.writeGuest([])),
        catchError(() => of([] as string[]))
      )
      .subscribe((regs) => this.saved$.next(new Set(regs)));
  }

  /** Save or un-save. Never opens a sign-in box: guests save locally. */
  public toggle(reg: string, term?: string): Observable<boolean> {
    return this.isSaved(reg) ? this.unsave(reg) : this.save(reg, term);
  }

  public save(reg: string, term?: string): Observable<boolean> {
    const r = SavedPlatesService.canonical(reg);
    if (!r || this.inFlight.has(r)) return of(false);
    if (!this.session.isLoggedIn()) {
      this.writeGuest([...this.readGuest().filter((x) => x !== r), r]);
      this.saved$.next(new Set(this.saved$.value).add(r));
      return of(true);
    }
    this.inFlight.add(r);
    const call$ = this.legacy
      ? this.legacy.toggleFavourite(r, term)
      : this.account.savePlate(r, term).pipe(map(() => true), catchError(() => of(false)));
    return call$.pipe(
      tap((ok) => {
        this.inFlight.delete(r);
        if (ok) this.saved$.next(new Set(this.saved$.value).add(r));
      })
    );
  }

  public unsave(reg: string): Observable<boolean> {
    const r = SavedPlatesService.canonical(reg);
    if (!r || this.inFlight.has(r)) return of(false);
    const drop = () => {
      const next = new Set(this.saved$.value);
      next.delete(r);
      this.saved$.next(next);
    };
    if (!this.session.isLoggedIn()) {
      this.writeGuest(this.readGuest().filter((x) => x !== r));
      drop();
      return of(true);
    }
    this.inFlight.add(r);
    const call$ = this.legacy
      ? this.legacy.toggleFavourite(r)
      : this.account.unsavePlate(r).pipe(map(() => true), catchError(() => of(false)));
    return call$.pipe(
      tap((ok) => {
        this.inFlight.delete(r);
        if (ok) drop();
      })
    );
  }

  private readGuest(): string[] {
    try {
      const raw = JSON.parse(localStorage.getItem(SavedPlatesService.GUEST_KEY) || '[]');
      return Array.isArray(raw)
        ? raw.map((x) => SavedPlatesService.canonical(String(x))).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  }

  private writeGuest(regs: string[]): void {
    try {
      if (regs.length) localStorage.setItem(SavedPlatesService.GUEST_KEY, JSON.stringify(regs));
      else localStorage.removeItem(SavedPlatesService.GUEST_KEY);
    } catch {
      /* storage unavailable: the save lasts for this page only */
    }
  }
}
